import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { db, rtdb } from "../lib/firebase";
import { collection, query as fsQuery, where, limit as fsLimit, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, get, child, remove } from "firebase/database";
import {
  FiUsers,
  FiUser,
  FiUserCheck,
  FiPhone,
  FiVideo,
  FiMic,
  FiFilter,
  FiRefreshCw,
  FiActivity,
  FiTrash2,
} from "react-icons/fi";
import { IoMdCall } from "react-icons/io";
import { IoVideocam } from "react-icons/io5";
import UserDetailsModal from "../components/UserDetailsModal";

/* ================================
   AXIOS INSTANCE
================================ */
const baseURL= "/api/v1/admin/userActivity";


const UserActivity = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [engagements, setEngagements] = useState([]);
  const [fbOnlineUsers, setFbOnlineUsers] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [fbOnlineIds, setFbOnlineIds] = useState(new Set());
  const [stats, setStats] = useState({
    totalOnline: 0,
    maleOnline: 0,
    femaleOnline: 0,
    activeCalls: 0,
    audioCalls: 0,
    videoCalls: 0,
  });

  // Filters
  const [genderFilter, setGenderFilter] = useState("all");
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("live");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewUser, setViewUser] = useState(null);
  const [statFilter, setStatFilter] = useState("all");
  const [refreshCountdown, setRefreshCountdown] = useState(10);

  useEffect(() => {
    setStatFilter("all");
  }, [activeTab]);

  const adminUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canManageStatus = adminUser?.email === "admin@arrowcoders.com";

  /* ================================
     FETCH ALL USER ACTIVITY DATA
  ================================ */
  const fetchUserActivity = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      // 1️⃣ SUMMARY
      const summaryRes = await axios.get(baseURL + "/summary");
      const summary = summaryRes.data.data;

      setStats({
        totalOnline: summary.onlineUsers,
        maleOnline: summary.maleOnline,
        femaleOnline: summary.femaleOnline,
        activeCalls: summary.activeCalls,
        audioCalls: summary.audioCalls,
        videoCalls: summary.videoCalls,
      });

 

      // 3️⃣ IN-CALL USERS
      const callRes = await axios.get(baseURL + "/user-active-call");
      
      // Calculate server time offset to fix timer drift
      const serverDateStr = callRes.headers.date;
      if (serverDateStr) {
        const serverTime = new Date(serverDateStr).getTime();
        const clientTime = Date.now();
        setServerTimeOffset(serverTime - clientTime);
      }

      setActiveCalls(callRes.data.data || []);

      // 4️⃣ ENGAGEMENTS
      const engagementRes = await axios.get( baseURL+"/engagements");
      setEngagements(engagementRes.data.data || []);
    } catch (error) {
      console.error("❌ Error fetching user activity:", error);
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  /* ================================
     AUTO REFRESH
  ================================ */
  useEffect(() => {
    fetchUserActivity(true);
    const interval = setInterval(() => fetchUserActivity(false), 10000);
    return () => clearInterval(interval);
  }, [genderFilter]);

  const handleEndCall = async (e, callTransactionId) => {
    e.stopPropagation();
    if (!callTransactionId) return;
    const ok = window.confirm("Are you sure you want to end this call?");
    if (!ok) return;
    try {
      await axios.delete(`${baseURL}/end-call/${callTransactionId}`);
      await fetchUserActivity(false);
    } catch (err) {
      console.error("End call failed:", err);
      alert(err?.response?.data?.message || "Failed to end call");
    }
  };

  const handleManualRefresh = () => {
    fetchUserActivity(false);
    if (activeTab === "live") {
      fetchLiveUsersFromRTDB();
      setRefreshCountdown(10); // Reset countdown on manual refresh
    }
  };

  const handleSetUserOffline = async (e, userId, source = "RTDB") => {
    e.stopPropagation();
    if (!userId) return;
    
    const label = source === "RTDB" ? "Live (RTDB)" : "Online (Firestore)";
    const ok = window.confirm(`Are you sure you want to set this user offline? (Removes from ${label})`);
    if (!ok) return;

    try {
      if (source === "RTDB") {
        if (!rtdb) throw new Error("RTDB not initialized");
        const userRef = ref(rtdb, `ActiveUsers/${userId}`);
        await remove(userRef);
      } else {
        if (!db) throw new Error("Firestore not initialized");
        // Update isOnline status in Firestore
        const userDocRef = doc(db, "users", userId.toString());
        await updateDoc(userDocRef, { isOnline: false });
      }
      
      alert(`User set offline from ${label} successfully`);
      handleManualRefresh();
    } catch (err) {
      console.error(`Set offline failed for ${source}:`, err);
      alert(`Failed to set user offline: ${err.message}`);
    }
  };

  const handleSetAllOffline = async () => {
    const source = activeTab === "live" ? "RTDB" : null;
    if (!source) return;

    const label = "Live Users (RTDB)";
    const users = liveUsers;

    if (!users || users.length === 0) {
      alert(`No users found in ${label} tab to set offline.`);
      return;
    }

    const ok = window.confirm(`Are you sure you want to set ALL ${users.length} users offline from ${label}?`);
    if (!ok) return;

    setRefreshing(true);
    try {
      if (!rtdb) throw new Error("RTDB not initialized");
      const updates = {};
      users.forEach((u) => {
        if (u.id) updates[`ActiveUsers/${u.id}`] = null;
      });
      const { update: rtdbUpdate } = await import("firebase/database");
      await rtdbUpdate(ref(rtdb), updates);

      alert(`Successfully set all ${users.length} users offline from ${label}`);
      handleManualRefresh();
    } catch (err) {
      console.error(`Set all offline failed:`, err);
      alert(`Failed to set all users offline: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  /* ================================
     USER ENRICHMENT HELPER
  ================================ */
  const enrichUserRecord = async (r) => {
    try {
      const id = (r.id || r._id || "").toString();
      if (!id) return r;

      // Skip if already fully populated
      if (r.fullName && r.gender && r.image && r.fullName.trim().toLowerCase() !== "unknown" && r.gender !== "-") {
        return r;
      }

      let u = null;

      // 1️⃣ Try Admin API by ID
      try {
        const res = await axios.get(`/api/v1/admin/users/${id}`);
        u = res?.data?.data || res?.data || null;
      } catch (_) {}

      // 2️⃣ Try Firestore if API failed or fields missing
      if (!u || !u.fullName || !u.gender || u.gender === "-" || !u.image) {
        try {
          // A. Try by document ID
          const docSnap = await getDoc(doc(db, "users", id));
          if (docSnap.exists()) {
            u = { ...u, ...docSnap.data() };
          } else {
            // B. Try by userId field
            const usersCol = collection(db, "users");
            const snapByField = await getDocs(fsQuery(usersCol, where("userId", "==", id), fsLimit(1)));
            if (!snapByField.empty) {
              u = { ...u, ...snapByField.docs[0].data() };
            } else if (!isNaN(id)) {
              // C. Try as numeric userId if applicable
              const snapByNum = await getDocs(fsQuery(usersCol, where("userId", "==", Number(id)), fsLimit(1)));
              if (!snapByNum.empty) {
                u = { ...u, ...snapByNum.docs[0].data() };
              }
            }
          }
        } catch (_) {}
      }

      // 3️⃣ Try Search API by Phone as final fallback
      if ((!u || !u.fullName || !u.gender || u.gender === "-") && r.phoneNumber) {
        try {
          const searchRes = await axios.get("/api/v2/admin/user", {
            params: { page: 1, limit: 1, search: r.phoneNumber },
          });
          const found = Array.isArray(searchRes?.data?.data) ? searchRes.data.data[0] : null;
          if (found) u = { ...u, ...found };
        } catch (_) {}
      }

      if (u) {
        const name = u.fullName || u.name || u.displayName || "";
        const gender = u.gender || "";
        const phone = u.phoneNumber || u.phone || "";
        const image = u.image || u.profileImage || u.avatar || u.profilePhoto || u.photoURL || "";

        if (name && name.trim().toLowerCase() !== "unknown") {
          r.fullName = name.trim();
        }
        if (gender && gender !== "-") {
          r.gender = gender.toLowerCase();
        }
        if (phone) {
          r.phoneNumber = phone;
        }
        if (image) {
          r.image = image;
        }
      }
      return r;
    } catch (err) {
      console.error("Enrichment error for", r.id, err);
      return r;
    }
  };

  const fetchFirebaseOnlineUsers = async () => {
    try {
      setRefreshing(true);
      if (!db) {
        setFbOnlineUsers([]);
        return;
      }
      const base = collection(db, "users");
      const qRef = fsQuery(base, where("isOnline", "==", true), fsLimit(200));
      const snap = await getDocs(qRef);
      let records = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: data.userId || d.id,
          fullName: data.fullName || data.name || "",
          phoneNumber: data.phoneNumber || data.phone || "",
          gender: data.gender || "",
          image:
            data.image ||
            data.profileImage ||
            data.photoURL ||
            data.userImage ||
            ""
        };
      });

      const needEnrich = records
        .filter(
          (r) =>
            !r.fullName ||
            r.fullName.trim().toLowerCase() === "unknown" ||
            !r.gender ||
            r.gender === "-" ||
            !r.image
        );

      // Process enrichment in parallel chunks
      await Promise.all(needEnrich.map(r => enrichUserRecord(r)));
      
      setFbOnlineUsers(records);
    } catch {
      setFbOnlineUsers([]);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchLiveUsersFromRTDB = async () => {
    try {
      setRefreshing(true);
      if (!rtdb) {
        console.warn("RTDB not initialized");
        setLiveUsers([]);
        return;
      }

      const dbRef = ref(rtdb);
      const snapshot = await get(child(dbRef, "ActiveUsers"));

      if (snapshot.exists()) {
        const data = snapshot.val();
        const now = Date.now();
        const HEARTBEAT_TIMEOUT = 10000; // 10 seconds - consider offline if no update in last 10s

        let records = Object.keys(data)
          .map((id) => {
            const userData = data[id];
            
            // Check if user has isOnline flag set to false
            if (userData.isOnline === false) return null;
            
            // Check heartbeat timestamps including updatedAt
            const lastSeen = userData.lastSeen || 0;
            const lastActive = userData.lastActive || 0;
            const updatedAt = userData.updatedAt || 0;
            
            // If no updatedAt field exists, consider user offline
            if (!updatedAt || updatedAt === 0) {
              console.log(`User ${id} is offline - no updatedAt field`);
              return null;
            }
            
            // Get the most recent timestamp from all three fields
            const mostRecentTimestamp = Math.max(lastSeen, lastActive, updatedAt);
            
            // If no recent heartbeat, consider offline
            if (mostRecentTimestamp === 0 || (now - mostRecentTimestamp) > HEARTBEAT_TIMEOUT) {
              console.log(`User ${id} is offline - no recent heartbeat (last: ${new Date(mostRecentTimestamp).toISOString()})`);
              return null;
            }
            
            // Determine busy status from callType field
            // Four call types: "audio", "RandomAudio", "video", "RandomVideo"
            const callType = userData.callType || "";
            
            // Check for audio, video, RandomAudio, or RandomVideo (case-sensitive)
            const isAudioCall = callType === "audio" || callType === "RandomAudio";
            const isVideoCall = callType === "video" || callType === "RandomVideo";
            const isBusy = isAudioCall || isVideoCall || !!userData.isBusy;
            
            // Determine display call type
            let displayCallType = "";
            let callTypeLabel = "";
            if (callType === "audio") {
              displayCallType = "audio";
              callTypeLabel = "Audio Call";
            } else if (callType === "RandomAudio") {
              displayCallType = "randomAudio";
              callTypeLabel = "Random Audio";
            } else if (callType === "video") {
              displayCallType = "video";
              callTypeLabel = "Video Call";
            } else if (callType === "RandomVideo") {
              displayCallType = "randomVideo";
              callTypeLabel = "Random Video";
            } else {
              displayCallType = callType;
              callTypeLabel = callType;
            }
            
            return {
              id,
              ...userData,
              fullName: userData.name || userData.fullName || "",
              phoneNumber: userData.number || userData.phoneNumber || "",
              isBusy: isBusy,
              callType: displayCallType,
              callTypeLabel: callTypeLabel,
              originalCallType: callType, // Keep original for reference
              image: userData.image || userData.profileImage || "",
              lastSeen: lastSeen,
              lastActive: lastActive,
              updatedAt: updatedAt,
              lastHeartbeat: mostRecentTimestamp,
            };
          })
          .filter(u => u !== null); // Remove null entries (offline users)

        console.log(`Found ${records.length} live users in RTDB with active heartbeat (last 10s)`);

        const needEnrich = records;
        await Promise.all(needEnrich.map(r => enrichUserRecord(r)));
        
        setLiveUsers(records);
      } else {
        console.log("No ActiveUsers data in RTDB");
        setLiveUsers([]);
      }
    } catch (error) {
      console.error("RTDB Fetch error:", error);
      setLiveUsers([]);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchFirebaseOnlineIds = async () => {
    try {
      if (!db) {
        setFbOnlineIds(new Set());
        return;
      }
      const base = collection(db, "users");
      const qRef = fsQuery(base, where("isOnline", "==", true), fsLimit(1000));
      const snap = await getDocs(qRef);
      const ids = new Set(snap.docs.map((d) => (d.data()?.userId || d.id)?.toString()));
      setFbOnlineIds(ids);
    } catch {
      setFbOnlineIds(new Set());
    }
  };

  useEffect(() => {
    if (activeTab === "live") {
      fetchLiveUsersFromRTDB();
      setRefreshCountdown(10);
      
      // Countdown timer
      const countdownInterval = setInterval(() => {
        setRefreshCountdown((prev) => {
          if (prev <= 1) {
            return 10;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Data refresh
      const refreshInterval = setInterval(() => {
        fetchLiveUsersFromRTDB();
        setRefreshCountdown(10);
      }, 10000);
      
      return () => {
        clearInterval(countdownInterval);
        clearInterval(refreshInterval);
      };
    }
  }, [activeTab]);

  useEffect(() => {
    fetchFirebaseOnlineIds();
    const interval = setInterval(fetchFirebaseOnlineIds, 100000);
    return () => clearInterval(interval);
  }, []);

  /* ================================
     FILTERS
  ================================ */
  const filteredOnlineUsers = useMemo(() => {
    if (!Array.isArray(onlineUsers)) return [];
    return onlineUsers.filter((u) => {
      const uid = (u._id || u.id || "").toString();
      const isOn = fbOnlineIds.has(uid);
      const gender = String(u.gender || "").toLowerCase();
      
      const statGenderOk = 
        statFilter === "male" ? gender === "male" :
        statFilter === "female" ? gender === "female" :
        statFilter === "unknown" ? (gender !== "male" && gender !== "female") :
        true;

      const statStatusOk =
        statFilter === "busy" ? !!u.isBusy :
        statFilter === "available" ? !u.isBusy :
        true;

      const genderOk =
        genderFilter === "all"
          ? true
          : gender === genderFilter;

      const statusOk =
        statusFilter === "all"
          ? true
          : statusFilter === "online"
          ? isOn
          : !isOn;

      return statGenderOk && statStatusOk && genderOk && statusOk;
    });
  }, [onlineUsers, fbOnlineIds, statFilter, genderFilter, statusFilter]);

  const filteredFbOnlineUsers = useMemo(() => {
    if (!Array.isArray(fbOnlineUsers)) return [];
    return fbOnlineUsers.filter((u) => {
      const gender = String(u.gender || "").toLowerCase();
      const isOnline = fbOnlineIds.has((u.id || "").toString());

      const statGenderOk = 
        statFilter === "male" ? gender === "male" :
        statFilter === "female" ? gender === "female" :
        statFilter === "unknown" ? (gender !== "male" && gender !== "female") :
        true;

      const statStatusOk =
        statFilter === "busy" ? !!u.isBusy :
        statFilter === "available" ? !u.isBusy :
        true;

      return statGenderOk && statStatusOk;
    });
  }, [fbOnlineUsers, statFilter, fbOnlineIds]);

  const filteredLiveUsers = useMemo(() => {
    if (!Array.isArray(liveUsers)) return [];
    return liveUsers.filter((u) => {
      const gender = String(u.gender || "").toLowerCase();

      const statGenderOk = 
        statFilter === "male" ? gender === "male" :
        statFilter === "female" ? gender === "female" :
        statFilter === "unknown" ? (gender !== "male" && gender !== "female") :
        true;

      const statStatusOk =
        statFilter === "busy" ? !!u.isBusy :
        statFilter === "available" ? !u.isBusy :
        true;

      return statGenderOk && statStatusOk;
    });
  }, [liveUsers, statFilter]);

  const offlineUsersAll = Array.isArray(onlineUsers)
    ? onlineUsers.filter((u) => {
        const uid = (u._id || u.id || "").toString();
        return !fbOnlineIds.has(uid);
      })
    : [];
  const offlineMaleCount = offlineUsersAll.filter(
    (u) => String(u.gender || "").toLowerCase() === "male"
  ).length;
  const offlineFemaleCount = offlineUsersAll.filter(
    (u) => String(u.gender || "").toLowerCase() === "female"
  ).length;
  const offlineTotal = offlineUsersAll.length;

  const onlineUsersAllCalc = Array.isArray(onlineUsers)
    ? onlineUsers.filter((u) => {
        const uid = (u._id || u.id || "").toString();
        return fbOnlineIds.has(uid);
      })
    : [];
  const onlineMaleCount = onlineUsersAllCalc.filter(
    (u) => String(u.gender || "").toLowerCase() === "male"
  ).length;
  const onlineFemaleCount = onlineUsersAllCalc.filter(
    (u) => String(u.gender || "").toLowerCase() === "female"
  ).length;
  const onlineTotal = onlineUsersAllCalc.length;
  const filteredActiveCalls = activeCalls.filter((call) => {
    if (callTypeFilter === "all") return true;
    return call.callType === callTypeFilter;
  });

  const activeUsersStats = useMemo(() => {
    // Calculate stats from the onlineUsers array (backend API data)
    const list = Array.isArray(onlineUsers) ? onlineUsers : [];
    const total = list.length;
    const male = list.filter((u) => String(u.gender || "").toLowerCase() === "male").length;
    const female = list.filter((u) => String(u.gender || "").toLowerCase() === "female").length;
    const unknown = Math.max(0, total - male - female);
    return { total, male, female, unknown };
  }, [onlineUsers]);

  const liveStats = useMemo(() => {
    const list = Array.isArray(liveUsers) ? liveUsers : [];
    const total = list.length;
    const male = list.filter((u) => String(u.gender || "").toLowerCase() === "male").length;
    const female = list.filter((u) => String(u.gender || "").toLowerCase() === "female").length;
    const unknown = Math.max(0, total - male - female);

    const busyUsers = list.filter((u) => !!u.isBusy);
    const busy = busyUsers.length;
    const busyMale = busyUsers.filter((u) => String(u.gender || "").toLowerCase() === "male").length;
    const busyFemale = busyUsers.filter((u) => String(u.gender || "").toLowerCase() === "female").length;
    const busyUnknown = Math.max(0, busy - busyMale - busyFemale);

    const availableUsers = list.filter((u) => !u.isBusy);
    const available = availableUsers.length;
    const availableMale = availableUsers.filter((u) => String(u.gender || "").toLowerCase() === "male").length;
    const availableFemale = availableUsers.filter((u) => String(u.gender || "").toLowerCase() === "female").length;
    const availableUnknown = Math.max(0, available - availableMale - availableFemale);

    return {
      total, male, female, unknown,
      busy, busyMale, busyFemale, busyUnknown,
      available, availableMale, availableFemale, availableUnknown
    };
  }, [liveUsers]);

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const CallDurationTimer = ({ startedAt, serverOffset = 0 }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
      const start = new Date(startedAt).getTime();
      const update = () => {
        // Correct the current time with the server offset
        const now = Date.now() + serverOffset;
        setElapsed(Math.max(0, Math.floor((now - start) / 1000)));
      };
      update();
      const timer = setInterval(update, 1000);
      return () => clearInterval(timer);
    }, [startedAt, serverOffset]);

    const format = (s) => {
      if (s < 0) s = 0;
      const hrs = Math.floor(s / 3600);
      const mins = Math.floor((s % 3600) / 60);
      const secs = s % 60;
      return `${hrs > 0 ? `${hrs}:` : ""}${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    return (
      <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-1 rounded-md w-fit border border-emerald-100">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
        </span>
        <span>{format(elapsed)}</span>
      </div>
    );
  };

  const resolveDisplayName = (obj) => {
    const idStr = (obj?._id || obj?.id || "").toString();
    const pick = (v) =>
      typeof v === "string" &&
      v.trim().length > 0 &&
      v.trim().toLowerCase() !== "unknown" &&
      v.trim() !== "-";
    const candidates = [obj?.fullName, obj?.name, obj?.phoneNumber, obj?.email];
    const found = candidates.find(pick);
    if (found) return found.trim();
    if (idStr) return `User ${idStr.slice(-4)}`;
    return "User";
  };

  const openUserModal = async (u) => {
    if (!u) return;
    const id = u._id || u.id;
    // Optimistic model from available data
    const baseModel = {
      name: resolveDisplayName(u),
      email: u.email || "",
      phone: u.phoneNumber || u.phone || "",
      gender: u.gender || "",
      image:
        u.image ||
        u.profileImage ||
        u.avatar ||
        u.profilePhoto ||
        u.photoURL ||
        "",
      role: u.role || "user",
      address: u.address || "",
      latitude: u.latitude || "",
      longitude: u.longitude || "",
      age: u.age || "",
      dob: u.dateOfBirth || u.dob || "",
    };
    setViewUser({ userId: id, model: { ...baseModel } });

    // Enrich from backend by id; fallback to search by phone/email
    try {
      let userRes = null;
      if (id) {
        try {
          userRes = await axios.get(`/api/v1/admin/users/${id}`);
        } catch (_) {
          userRes = null;
        }
      }
      let backendUser = userRes?.data?.data || userRes?.data || null;

      // Fallback to Firestore if API failed
      if (!backendUser && id) {
        try {
          const docSnap = await getDoc(doc(db, "users", id.toString()));
          if (docSnap.exists()) {
            backendUser = docSnap.data();
          } else {
            const fsSnap = await getDocs(fsQuery(collection(db, "users"), where("userId", "==", id.toString()), fsLimit(1)));
            if (!fsSnap.empty) {
              backendUser = fsSnap.docs[0].data();
            }
          }
        } catch (_) {}
      }

      if (!backendUser && (baseModel.phone || baseModel.email)) {
        const q = baseModel.phone || baseModel.email;
        try {
          const searchRes = await axios.get("/api/v2/admin/user", {
            params: { page: 1, limit: 1, search: q },
          });
          const found = Array.isArray(searchRes?.data?.data) ? searchRes.data.data[0] : null;
          if (found) backendUser = found;
        } catch (_) {}
      }
      if (backendUser) {
        const enriched = {
          name: resolveDisplayName(backendUser) || baseModel.name || "",
          email: backendUser.email || baseModel.email || "",
          phone: backendUser.phoneNumber || backendUser.phone || baseModel.phone || "",
          gender: backendUser.gender || baseModel.gender || "",
          image:
            backendUser.image ||
            backendUser.profileImage ||
            backendUser.avatar ||
            backendUser.profilePhoto ||
            backendUser.photoURL ||
            baseModel.image ||
            "",
          role: backendUser.role || baseModel.role,
          address: backendUser.address || baseModel.address || "",
          latitude: backendUser.latitude || baseModel.latitude || "",
          longitude: backendUser.longitude || baseModel.longitude || "",
          age: backendUser.age || baseModel.age || "",
          dob: backendUser.dateOfBirth || backendUser.dob || baseModel.dob || "",
        };
        setViewUser({ userId: backendUser._id || id, model: enriched });
      }
    } catch (_) {
      // keep optimistic
    }
  };

  /* ================================
     UI
  ================================ */
  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3">
              <FiActivity className="text-3xl" />
              User Activity
            </h1>
            <p className="text-[var(--black-text)]/70 mt-2">
              Monitor online users and active call activities in real-time
            </p>
          </div>

          <button
            onClick={handleManualRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white rounded-lg"
          >
            <FiRefreshCw />
            Refresh
          </button>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {activeTab === "calls" ? (
            // Stats for In-Call Activity tab
            [
              ["Active Calls", stats.activeCalls, <FiPhone />, "green", "all"],
              ["Audio Calls", stats.audioCalls, <FiMic />, "teal", "audio"],
              ["Video Calls", stats.videoCalls, <FiVideo />, "purple", "video"],
            ].map(([title, value, icon, color, filterType], i) => {
              const isActive = statFilter === filterType;
              const colorClasses = {
                green: {
                  bg: isActive ? "bg-green-500/20 border-green-500" : "bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20",
                  text: "text-green-400",
                  icon: "text-green-400"
                },
                teal: {
                  bg: isActive ? "bg-teal-500/20 border-teal-500" : "bg-gradient-to-br from-teal-500/10 to-teal-500/5 border-teal-500/20",
                  text: "text-teal-400",
                  icon: "text-teal-400"
                },
                purple: {
                  bg: isActive ? "bg-purple-500/20 border-purple-500" : "bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20",
                  text: "text-purple-400",
                  icon: "text-purple-400"
                }
              };

              return (
                <button
                  key={i}
                  onClick={() => setStatFilter(isActive ? "all" : filterType)}
                  className={`${colorClasses[color].bg} border-2 rounded-xl p-2 shadow-lg transition-all duration-200 transform hover:scale-[1.02] text-left cursor-pointer focus:outline-none`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs font-medium ${colorClasses[color].text} uppercase tracking-wider`}>
                        {title}
                      </p>
                      <p className={`text-2xl font-bold ${colorClasses[color].text} leading-none mb-1`}>
                        {value}
                      </p>
                    </div>
                    <div className={`text-3xl ${colorClasses[color].icon}`}>
                      {icon}
                    </div>
                  </div>
                </button>
              );
            })
          ) : (
            // Stats for Live Users and Active Users tabs
            [
              [
                activeTab === "live"
                  ? "Live Users"
                  : activeTab === "online"
                  ? "Active Users"
                  : "Active Users",
                activeTab === "live"
                  ? liveStats.total
                  : activeTab === "online"
                  ? activeUsersStats.total
                  : stats.totalOnline,
                <FiUsers />,
                "yellow",
                "all",
              ],
              [
                activeTab === "live"
                  ? "Live Male"
                  : activeTab === "online"
                  ? "Active Male"
                  : "Active Male",
                activeTab === "live"
                  ? liveStats.male
                  : activeTab === "online"
                  ? activeUsersStats.male
                  : stats.maleOnline,
                <FiUser />,
                "blue",
                "male",
              ],
              [
                activeTab === "live"
                  ? "Live Female"
                  : activeTab === "online"
                  ? "Active Female"
                  : "Active Female",
                activeTab === "live"
                  ? liveStats.female
                  : activeTab === "online"
                  ? activeUsersStats.female
                  : stats.femaleOnline,
                <FiUserCheck />,
                "purple",
                "female",
              ],
              [
                activeTab === "live"
                  ? "Live Unknown"
                  : activeTab === "online"
                  ? "Active Unknown"
                  : "Active Unknown",
                activeTab === "live"
                  ? liveStats.unknown
                  : activeTab === "online"
                  ? activeUsersStats.unknown
                  : (stats.totalOnline - stats.maleOnline - stats.femaleOnline),
                <FiUser />,
                "gray",
                "unknown",
              ],
              [
                activeTab === "live" ? "Busy Users" : "Active Calls",
                activeTab === "live" ? liveStats.busy : stats.activeCalls,
                <FiPhone />,
                activeTab === "live" ? "orange" : "green",
                "busy",
                activeTab === "live" ? { male: liveStats.busyMale, female: liveStats.busyFemale, unknown: liveStats.busyUnknown } : null
              ],
              [
                activeTab === "live" ? "Available Users" : "Audio Calls",
                activeTab === "live" ? liveStats.available : stats.audioCalls,
                <FiMic />,
                activeTab === "live" ? "green" : "teal",
                "available",
                activeTab === "live" ? { male: liveStats.availableMale, female: liveStats.availableFemale, unknown: liveStats.availableUnknown } : null
              ],
            ].map(([title, value, icon, color, filterType, breakdown], i) => {
              const isActive = statFilter === filterType;
              const colorClasses = {
                yellow: {
                  bg: isActive ? "bg-[var(--yellow)]/20 border-[var(--yellow)]" : "bg-gradient-to-br from-[var(--yellow)]/10 to-[var(--yellow)]/5 border-[var(--yellow)]/20",
                  text: "text-[var(--yellow)]",
                  icon: "text-[var(--yellow)]"
                },
                blue: {
                  bg: isActive ? "bg-blue-500/20 border-blue-500" : "bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20",
                  text: "text-blue-400",
                  icon: "text-blue-400"
                },
                purple: {
                  bg: isActive ? "bg-purple-500/20 border-purple-500" : "bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20",
                  text: "text-purple-400",
                  icon: "text-purple-400"
                },
                green: {
                  bg: isActive ? "bg-green-500/20 border-green-500" : "bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20",
                  text: "text-green-400",
                  icon: "text-green-400"
                },
                teal: {
                  bg: isActive ? "bg-teal-500/20 border-teal-500" : "bg-gradient-to-br from-teal-500/10 to-teal-500/5 border-teal-500/20",
                  text: "text-teal-400",
                  icon: "text-teal-400"
                },
                orange: {
                  bg: isActive ? "bg-orange-500/20 border-orange-500" : "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20",
                  text: "text-orange-400",
                  icon: "text-orange-400"
                },
                gray: {
                  bg: isActive ? "bg-gray-500/20 border-gray-500" : "bg-gradient-to-br from-gray-500/10 to-gray-500/5 border-gray-500/20",
                  text: "text-gray-400",
                  icon: "text-gray-400"
                }
              };

              return (
                <button
                  key={i}
                  onClick={() => setStatFilter(isActive ? "all" : filterType)}
                  className={`${colorClasses[color].bg} border-2 rounded-xl p-2 shadow-lg transition-all duration-200 transform hover:scale-[1.02] text-left cursor-pointer focus:outline-none`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs font-medium ${colorClasses[color].text} uppercase tracking-wider`}>
                        {title}
                      </p>
                      <p className={`text-2xl font-bold ${colorClasses[color].text} leading-none mb-1`}>
                        {value}
                      </p>
                      {breakdown && (
                        <div className="flex gap-2">
                          <span className="text-[9px] font-bold opacity-80 bg-blue-500/10 px-1 py-0.5 rounded text-blue-400">M: {breakdown.male}</span>
                          <span className="text-[9px] font-bold opacity-80 bg-purple-500/10 px-1 py-0.5 rounded text-purple-400">F: {breakdown.female}</span>
                          {breakdown.unknown > 0 && (
                            <span className="text-[9px] font-bold opacity-80 bg-gray-500/10 px-1 py-0.5 rounded text-gray-400">U: {breakdown.unknown}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`text-3xl ${colorClasses[color].icon}`}>
                      {icon}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* TABS */}
        <div className="border-2 border-gray-200 rounded-xl p-6">
          <div className="flex gap-2 items-center border-b-2 mb-6 justify-between">
            <div className="flex gap-2">
              {["live", "calls"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 font-semibold border-b-2 ${
                    activeTab === tab
                      ? "border-[var(--primary-solid)] text-[var(--primary-solid)]"
                      : "border-transparent text-gray-400"
                  }`}
                >
                  {tab === "online" && `Active Users (${filteredOnlineUsers.length})`}
                  {tab === "live" && `Live Users (${liveUsers.length})`}
                  {tab === "calls" && `In-Call Activity (${filteredActiveCalls.length})`}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 pr-2">
              {activeTab === "live" && (
                <span className="text-xs text-gray-500 flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                  </span>
                  <span className="font-mono font-bold text-blue-600">{refreshCountdown}s</span>
                </span>
              )}
              {canManageStatus && activeTab === "live" && (
                <button
                  onClick={handleSetAllOffline}
                  className="flex items-center gap-2 px-4 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-lg text-sm font-bold border border-orange-200 transition-colors"
                  title="Set all users in Live tab offline"
                >
                  <FiTrash2 size={16} />
                  <span>Set All Offline</span>
                </button>
              )}
              {refreshing && (
                <span className="inline-block w-4 h-4 border-2 border-gray-300 border-top-color-[var(--primary-solid)] border-t-[var(--primary-solid)] rounded-full animate-spin" />
              )}
            </div>
          </div>

          {/* ONLINE USERS */}
          {activeTab === "online" && (
            <>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
             
                </div>
                <div className="flex items-center">
                  <FiFilter />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="ml-2 border-2 p-2 rounded"
                  >
                    <option value="all">All Status</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                  <select
                    value={genderFilter}
                    onChange={(e) => setGenderFilter(e.target.value)}
                    className="ml-2 border-2 p-2 rounded"
                  >
                    <option value="all">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              {filteredOnlineUsers.length === 0 ? (
                <p className="text-center text-gray-500">No online users found</p>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  {filteredOnlineUsers.map((user) => {
                    const uid = (user._id || user.id || "").toString();
                    const isOnline = fbOnlineIds.has(uid);
                    const image =
                      user.image ||
                      user.profileImage ||
                      user.avatar ||
                      user.profilePhoto ||
                      user.photoURL ||
                      "";
                    const name = resolveDisplayName({ ...user, _id: user._id || user.id });
                    const initial = name.charAt(0).toUpperCase();
                    const gender = user.gender || "";
                    return (
                      <div
                        key={user._id}
                        className="relative border-2 p-4 rounded-xl shadow-sm hover:shadow-md transition bg-white cursor-pointer"
                        onClick={() => openUserModal(user)}
                      >
                        {/* Set Offline Button */}
                        {canManageStatus && (
                          <button
                            onClick={(e) => handleSetUserOffline(e, user._id || user.id, "Firestore")}
                            className="absolute top-3 right-3 p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors z-10"
                            title="Set User Offline"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        )}

                        <div className="flex items-center gap-4">
                          <div className="relative">
                            {image ? (
                              <img
                                src={image}
                                alt={name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-semibold">
                                {initial}
                              </div>
                            )}
                            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${isOnline ? "bg-green-500" : "bg-gray-300"} border-2 border-white rounded-full`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-pink-500 font-semibold">
                              {name}
                            </div>
                            <div className="text-xs text-cyan-500 mt-0.5 flex items-center gap-1">
                              <FiPhone className="opacity-70" />
                              <span>{user.phoneNumber || ""}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-600 capitalize">
                                {gender || "-"}
                              </span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${isOnline ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                {isOnline ? "Online" : "Offline"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={`flex items-center justify-between mt-3 ${isOnline ? "text-green-600" : "text-gray-500"} text-sm`}>
                          <div className="flex items-center gap-2">
                            <FiUserCheck />
                            <span>{isOnline ? "Active Now" : "Inactive"}</span>
                          </div>
                          <FiActivity />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* CALLS */}
          {activeTab === "calls" && (
            <>
              <div className="flex justify-end mb-4">
                <FiFilter />
                <select
                  value={callTypeFilter}
                  onChange={(e) => setCallTypeFilter(e.target.value)}
                  className="ml-2 border-2 p-2 rounded"
                >
                  <option value="all">All</option>
                  <option value="audio">Audio</option>
                  <option value="video">Video</option>
                </select>
              </div>

              {filteredActiveCalls.length === 0 ? (
                <p className="text-center text-gray-500">No active calls</p>
              ) : (
                <div className="space-y-4">
              

                  {filteredActiveCalls.map((call) => (
  <div
    key={call.callTransactionId}
    className="border-2 p-4 rounded-xl shadow-sm bg-white"
  >
    <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-50">
      <div className="flex items-center gap-3">
        <div className="text-2xl text-[var(--primary-solid)]">
          {call.callType === "video" ? <IoVideocam /> : <IoMdCall />}
        </div>
        <span className="capitalize font-bold text-lg text-gray-800">
          {call.callType} Call
        </span>
      </div>
      <div className="flex items-center gap-3">
        <CallDurationTimer startedAt={call.startedAt} serverOffset={serverTimeOffset} />
        <button
          onClick={(e) => handleEndCall(e, call.callTransactionId)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-xs font-bold transition-colors"
          title="End Call"
        >
          <FiTrash2 size={13} />
          End Call
        </button>
      </div>
    </div>

    <div className="flex items-center justify-between">
      {/* Caller */}
      <div className="flex items-center gap-3">
        <img
          src={call?.caller?.profileImage || ""}
          alt={call.caller.name}
          className="w-10 h-10 rounded-full object-cover bg-gray-200"
        />
        <div>
          <p className="font-medium">{call.caller.name}</p>
          <p className="text-xs text-gray-500 capitalize">
            {call.caller.gender}
          </p>
        </div>
      </div>

      <span className="text-gray-400 text-sm">talking with</span>

      {/* Receiver */}
      <div className="flex items-center gap-3">
        <img
          src={call.receiver.profileImage || ""}
          alt={call.receiver.name}
          className="w-10 h-10 rounded-full object-cover bg-gray-200"
        />
        <div>
          <p className="font-medium">{call.receiver.name}</p>
          <p className="text-xs text-gray-500 capitalize">
            {call.receiver.gender}
          </p>
        </div>
      </div>
    </div>

    <p className="text-[10px] text-gray-400 mt-4 border-t pt-2 flex items-center justify-between">
      <span>Transaction: {call.callTransactionId?.slice(-8) || "N/A"}</span>
      <span>Started At: {new Date(call.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
    </p>
  </div>
))}
                </div>
              )}
            </>
          )}

          {activeTab === "live" && (
            <>
              {filteredLiveUsers.length === 0 ? (
                <p className="text-center text-gray-500">No live users found</p>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  {filteredLiveUsers.map((user) => {
                    const displayName = resolveDisplayName(user);
                    const initial = (displayName || "U").charAt(0).toUpperCase();
                    const isBusy = !!user.isBusy;
                    const callType = user.callType || "";
                    const lastHeartbeat = user.lastHeartbeat || user.lastSeen || user.lastActive || 0;
                    const timeSinceHeartbeat = lastHeartbeat ? Math.floor((Date.now() - lastHeartbeat) / 1000) : 0;
                    const heartbeatText = timeSinceHeartbeat < 5 ? "Just now" : 
                                         timeSinceHeartbeat < 10 ? `${timeSinceHeartbeat}s ago` : 
                                         "10s+ ago";
                    const isVeryRecent = timeSinceHeartbeat < 5;
                    
                    return (
                      <div
                        key={user.id}
                        className="relative border border-gray-100 p-3 rounded-lg shadow-sm hover:shadow-md transition bg-white cursor-pointer"
                        onClick={() => openUserModal({ ...user, _id: user.id })}
                      >
                        {/* Set Offline Button */}
                        {canManageStatus && (
                          <button
                            onClick={(e) => handleSetUserOffline(e, user.id, "RTDB")}
                            className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors z-10"
                            title="Set User Offline"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}

                        <div className="flex items-center gap-3">
                          <div className="relative">
                            {user.image ? (
                              <img
                                src={user.image}
                                alt={displayName || "User"}
                                className="w-12 h-12 rounded-full object-cover shadow-sm"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-50 text-gray-400 flex items-center justify-center text-lg font-bold border border-gray-100">
                                {initial}
                              </div>
                            )}
                            {isBusy ? (
                              <>
                                {callType === "audio" || callType === "randomAudio" ? (
                                  <span className={`absolute bottom-0 right-0 w-5 h-5 ${
                                    callType === "randomAudio" ? "bg-amber-500" : "bg-orange-500"
                                  } border-2 border-white rounded-full shadow-sm flex items-center justify-center`}>
                                    <FiMic className="text-white text-[10px]" />
                                  </span>
                                ) : callType === "video" || callType === "randomVideo" ? (
                                  <span className={`absolute bottom-0 right-0 w-5 h-5 ${
                                    callType === "randomVideo" ? "bg-indigo-500" : "bg-purple-500"
                                  } border-2 border-white rounded-full shadow-sm flex items-center justify-center`}>
                                    <FiVideo className="text-white text-[10px]" />
                                  </span>
                                ) : (
                                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-red-500 border-2 border-white rounded-full shadow-sm" />
                                )}
                              </>
                            ) : (
                              <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full shadow-sm" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-bold text-white truncate mb-1">
                              {displayName || "-"}
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md text-white ${
                                String(user.gender || "").toLowerCase() === 'female' 
                                  ? 'bg-pink-500' 
                                  : String(user.gender || "").toLowerCase() === 'male'
                                  ? 'bg-blue-500'
                                  : 'bg-gray-400'
                              }`}>
                                {user.gender && user.gender !== "-" ? user.gender : "Unknown"}
                              </span>
                              {isBusy ? (
                                <>
                                  {callType === "audio" ? (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-orange-500 text-white flex items-center gap-1">
                                      <FiMic />
                                      Audio Call
                                    </span>
                                  ) : callType === "randomAudio" ? (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-amber-500 text-white flex items-center gap-1">
                                      <FiMic />
                                      Random Audio
                                    </span>
                                  ) : callType === "video" ? (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-purple-500 text-white flex items-center gap-1">
                                      <FiVideo />
                                      Video Call
                                    </span>
                                  ) : callType === "randomVideo" ? (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-indigo-500 text-white flex items-center gap-1">
                                      <FiVideo />
                                      Random Video
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-red-500 text-white">
                                      Busy
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-emerald-500 text-white">
                                  Online
                                </span>
                              )}
                            </div>
                            {lastHeartbeat > 0 && (
                              <div className={`flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md ${
                                isVeryRecent ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                              }`}>
                                <span className="relative flex h-2 w-2">
                                  {isVeryRecent && (
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                  )}
                                  <span className={`relative inline-flex rounded-full h-2 w-2 ${
                                    isVeryRecent ? 'bg-green-500' : 'bg-gray-400'
                                  }`}></span>
                                </span>
                                <span className={`text-[10px] font-bold ${
                                  isVeryRecent ? 'text-green-700' : 'text-gray-600'
                                }`}>
                                  {heartbeatText}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

         
        </div>
        {viewUser && (
          <UserDetailsModal
            model={viewUser.model}
            userId={viewUser.userId}
            onClose={() => setViewUser(null)}
          />
        )}
      </div>
    </div>
  );
};

export default UserActivity;

